import { useMemo } from 'react';

import { InfiniteData, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { simulationsEndpoints as endpoints } from '@/api/endpoints';
import { API, FIVE_SECONDS, isRunTerminal } from '@/modules/simulations/hooks/useSimulations';
import type { RunsListParams, RunsListResponse } from '@/modules/simulations/types';

export const useSimulationRuns = (brainId?: string, params?: RunsListParams) => {
  const {
    data: runsData,
    status: listStatus,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      endpoints.runs(brainId!),
      params?.status,
      params?.with_fails,
      params?.trigger_type,
      params?.sort,
      params?.started_after,
      params?.started_before,
    ],
    queryFn: ({ pageParam }) => API.listRuns(brainId!, { ...params, cursor: pageParam?.cursor }),
    getNextPageParam: (lastPage: RunsListResponse) =>
      lastPage.pagination?.has_more && lastPage.pagination?.next_cursor
        ? { cursor: lastPage.pagination.next_cursor }
        : undefined,
    initialPageParam: {} as { cursor?: string },
    enabled: !!brainId,
    staleTime: 0,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const data = query.state.data as InfiniteData<RunsListResponse> | undefined;
      const hasActive = data?.pages.some((p) => p.runs.some((r) => !isRunTerminal(r.status)));
      return hasActive ? FIVE_SECONDS : false;
    },
  });

  const runs = useMemo(() => runsData?.pages.flatMap((p) => p.runs) ?? [], [runsData]);

  return { runsData, runs, listStatus, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage };
};
